import { useState } from "react";
import { Loader2 } from "lucide-react";
import { SummaryTile } from "./components/SummaryTile";
import { MeetingTypeBarCharts } from "./components/MeetingTypeBarCharts";
import { PieChartSection } from "./components/PieChartSection";
import { useDashboardData, useBusinesses } from "@/services/dashboard";

const TILES = [
  {
    key: "totalDemandForDU" as const,
    title: "Total Demand for DU",
    tooltipText: "Total demand for DU across all programs"
  },
  {
    key: "totalRoomNightsScheduled" as const,
    title: "Total Room Nights Scheduled",
    tooltipText: "Total room nights scheduled across all programs"
  },
  {
    key: "totalMeetingsRequested" as const,
    title: "Total # of Meetings Requested",
    tooltipText: "Total number of meetings requested"
  },
  {
    key: "totalMeetingsScheduled" as const,
    title: "Total # of Meetings Scheduled",
    tooltipText: "Total number of meetings scheduled"
  },
  {
    key: "usedPracticalCapcity" as const,
    title: "% Used of Practical Capacity",
    tooltipText: "Percentage of practical capacity used"
  }
];

export function DashboardPage() {
  const [topBusiness, setTopBusiness] = useState<string | null>(null);
  const [pieRoomBusiness, setPieRoomBusiness] = useState<string | null>(null);
  const [pieDuBusiness, setPieDuBusiness] = useState<string | null>(null);

  const hasTopFilter = topBusiness !== null;

  const mainQuery = useDashboardData(hasTopFilter, topBusiness);

  // Per-chart drilldown queries — only active when no top filter
  const pieRoomQuery = useDashboardData(false, pieRoomBusiness);
  const pieDuQuery = useDashboardData(false, pieDuBusiness);

  const { data: businessesMap } = useBusinesses();
  const allBusinesses = businessesMap ? Object.keys(businessesMap) : [];

  const data = mainQuery.data;
  const showInitialLoadingOverlay = mainQuery.isPending && !data;
  const showRefreshLoadingMessage = mainQuery.isFetching && !!data;

  // Room nights pie: top filter → businessLine from main query; per-chart → drilldown query
  const roomNightsData = data?.totalRoomNightsByBusiness ?? [];
  const roomNightsLineData = hasTopFilter
    ? (data?.totalRoomNightsByBusinessLine ?? [])
    : (pieRoomQuery.data?.totalRoomNightsByBusinessLine ?? []);

  // DU percentage pie: same pattern
  const duPercentData = data?.duPercentageRoomNightsByBusiness ?? [];
  const duPercentLineData = hasTopFilter
    ? (data?.duPercentageRoomNightsByBusinessLine ?? [])
    : (pieDuQuery.data?.duPercentageRoomNightsByBusinessLine ?? []);

  const handleTopBusinessChange = (value: string | null) => {
    setTopBusiness(value);
    // Sync pie chart selections when top filter changes
    setPieRoomBusiness(value);
    setPieDuBusiness(value);
  };

  return (
    <div className="dashboard">
      {showInitialLoadingOverlay && (
        <div className="loading-overlay-react" role="status" aria-live="polite">
          <Loader2
            className="h-10 w-10 animate-spin text-brand"
            aria-label="Loading dashboard"
          />
        </div>
      )}

      {showRefreshLoadingMessage && (
        <div className="dashboard-refresh-overlay" role="status" aria-live="polite">
          <div className="dashboard-refresh-message">
            <Loader2 className="h-5 w-5 animate-spin text-brand" aria-hidden="true" />
            <span>Loading updated dashboard data...</span>
          </div>
        </div>
      )}

      <div className="page-header">
        <h1>Draft Calendar Metrics Dashboard</h1>

        <div className="filters">
            <label htmlFor="top-business-filter" className="sr-only">
              Filter by business
            </label>
            <select
              id="top-business-filter"
              value={topBusiness ?? ""}
              onChange={(e) => handleTopBusinessChange(e.target.value || null)}
              className="dropdown"
            >
              <option value="">All of DU</option>
              {allBusinesses.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>

            <select
              aria-label="Fiscal year"
              defaultValue="FY 2027"
              className="dropdown fiscal-year"
            >
              <option>FY 2027</option>
            </select>
        </div>
      </div>

      <div className="main-content">
        {mainQuery.isError && (
          <p role="alert" className="dashboard-error-alert">
            Failed to load dashboard data. Please try refreshing the page.
          </p>
        )}

        {data && (
          <>
            <section aria-label="Summary metrics" className="stats-row">
              {TILES.map((tile) => (
                <SummaryTile
                  key={tile.key}
                  title={tile.title}
                  value={data[tile.key] ?? "—"}
                  tooltipText={tile.tooltipText}
                />
              ))}
            </section>

            <section aria-label="Programs by meeting type" className="charts-row">
              <MeetingTypeBarCharts data={data.totalProgramByMeetingType ?? []} />
            </section>

            <section aria-label="Room nights by business" className="charts-row">
              <PieChartSection
                title="Total # of Room Nights by Business"
                tooltipText="Total room nights broken down by business unit"
                data={roomNightsData}
                businessLineData={roomNightsLineData}
                allBusinesses={allBusinesses}
                otherHoverItems={data.otherBusinessTotalRoomNightHover}
                disabled={hasTopFilter}
                selectedBusiness={hasTopFilter ? topBusiness : pieRoomBusiness}
                onBusinessChange={setPieRoomBusiness}
              />
              <PieChartSection
                title="% of DU Room Nights by Business / Line"
                tooltipText="Percentage of DU room nights broken down by business unit"
                data={duPercentData}
                businessLineData={duPercentLineData}
                allBusinesses={allBusinesses}
                otherHoverItems={data.otherBusinessDUPercentageRoomNightsHover}
                disabled={hasTopFilter}
                selectedBusiness={hasTopFilter ? topBusiness : pieDuBusiness}
                onBusinessChange={setPieDuBusiness}
              />
            </section>

            {data.lastUpdated && (
              <div className="last-updated">
                <p>
                  <strong>Last updated:</strong> {data.lastUpdated}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
